import { X, CreditCard, Lock, ArrowLeft, AlertTriangle, Wallet } from 'lucide-react';
import { useState } from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { addOrderToHistory } from '@/app/utils/orderHistory';
import { UserPortal } from './UserPortal';

interface CheckoutFormProps {
  planName: string;
  planPrice: number;
  planDescription: string;
  onClose: () => void;
  onSuccess: (tier: string) => void;
}

export function CheckoutForm({ planName, planPrice, planDescription, onClose, onSuccess }: CheckoutFormProps) {
  const { tier: currentTier } = useSubscription();
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'paypal'>('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasAcknowledgedCancellation, setHasAcknowledgedCancellation] = useState(false);
  const [showPortal, setShowPortal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    cardName: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    zipCode: '',
    avatarId: '',
    script: '',
  });

  // Check if user has an existing paid subscription
  const hasExistingSubscription = currentTier !== 'free';

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, '');
    const chunks = cleaned.match(/.{1,4}/g);
    return chunks ? chunks.join(' ') : cleaned;
  };

  const formatExpiryDate = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  const handleCardNumberChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 16) {
      handleInputChange('cardNumber', formatCardNumber(cleaned));
    }
  };

  const handleExpiryChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 4) {
      handleInputChange('expiryDate', formatExpiryDate(cleaned));
    }
  };

  const handleCvvChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 4) {
      handleInputChange('cvv', cleaned);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (paymentMethod === 'card') {
      // Validate card form
      if (!formData.email || !formData.cardName || !formData.cardNumber || !formData.expiryDate || !formData.cvv || !formData.zipCode) {
        alert('Please fill in all required fields');
        return;
      }

      // Basic card number validation (should be 16 digits)
      const cleanCardNumber = formData.cardNumber.replace(/\s/g, '');
      if (cleanCardNumber.length !== 16) {
        alert('Please enter a valid 16-digit card number');
        return;
      }

      // Basic expiry validation
      if (formData.expiryDate.length !== 5) {
        alert('Please enter a valid expiry date (MM/YY)');
        return;
      }

      // Basic CVV validation
      if (formData.cvv.length < 3) {
        alert('Please enter a valid CVV');
        return;
      }
    }

    setIsProcessing(true);

    try {
      // TODO: Integrate with Stripe API for credit card processing
      // TODO: Integrate with PayPal SDK for PayPal processing
      // TODO: After successful payment:
      //   1. Send confirmation email to user
      //   2. Send copy to cara@oratf.info with subscription details
      //   3. Update user tier in database
      
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Get tier key from plan name
      const tierKey = planName.toLowerCase().replace(/\s+/g, '');
      
      // Store subscription info
      localStorage.setItem('userTier', tierKey);
      localStorage.setItem('subscriberEmail', formData.email);
      
      // Generate transaction ID
      const transactionId = `TXN-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
      
      // Add order to history
      addOrderToHistory({
        userId: `USER-${Date.now()}`,
        userEmail: formData.email,
        orderType: 'subscription',
        planName: planName,
        amount: planPrice,
        status: 'delivered',
        paymentMethod: paymentMethod === 'card' ? 'Credit Card' : 'PayPal',
        transactionId: transactionId,
        customerId: formData.email,
      });
      
      alert(`✅ Payment successful! Welcome to ${planName}!\n\nA confirmation email has been sent to ${formData.email}.`);
      
      // Call success callback with tier
      onSuccess(tierKey);
      
      // Show user portal after successful payment
      setShowPortal(true);
    } catch (error: any) {
      alert(`Payment failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePayPalCheckout = () => {
    setIsProcessing(true);
    
    // TODO: Integrate PayPal SDK
    // TODO: Initialize PayPal payment flow
    alert('PayPal integration will be implemented next.\n\nFor now, redirecting to PayPal checkout...');
    
    // Simulate PayPal redirect
    setTimeout(() => {
      setIsProcessing(false);
      // After successful PayPal payment, same flow as card payment
    }, 2000);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-2 overflow-y-auto">
        <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-300 my-2 max-h-[95vh] flex flex-col">
          {/* Header - Sticky */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-3 rounded-t-2xl relative flex-shrink-0">
            <button
              onClick={onClose}
              className="absolute top-2 left-2 text-white hover:bg-white/20 p-1.5 rounded-lg transition-colors"
            >
              <ArrowLeft className="size-4" />
            </button>
            <button
              onClick={onClose}
              className="absolute top-2 right-2 text-white hover:bg-white/20 p-1.5 rounded-lg transition-colors"
            >
              <X className="size-4" />
            </button>
            <div className="text-center pt-6">
              <h2 className="text-lg font-bold text-white mb-1">Complete Your Purchase</h2>
              <p className="text-blue-100 text-xs">
                Secure checkout powered by Stripe & PayPal
              </p>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="p-3 overflow-y-auto flex-1">
            {/* Order Summary */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-3 mb-3">
              <h3 className="font-bold text-sm text-black mb-2">Order Summary</h3>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-gray-700">{planName} Plan</span>
                <span className="font-bold text-sm text-black">${planPrice}/month</span>
              </div>
              <p className="text-[10px] text-gray-600 mb-2">{planDescription}</p>
              <div className="border-t border-blue-200 pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-sm text-blue-900">Total Due Today</span>
                  <span className="text-xl font-bold text-blue-600">${planPrice}</span>
                </div>
                <p className="text-[10px] text-gray-600 mt-1">Billed monthly. Cancel anytime.</p>
              </div>
            </div>

            {/* Existing Subscription Warning */}
            {hasExistingSubscription && (
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-400 rounded-xl p-3 mb-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="size-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-bold text-sm text-black mb-1.5 flex items-center gap-2">
                      Important: Existing Subscription Detected
                    </h3>
                    <p className="text-xs text-gray-800 mb-2">
                      You currently have an active <span className="font-bold text-black uppercase">{currentTier}</span> subscription. Please cancel your previous subscription before upgrading to avoid being charged for both plans.
                    </p>
                    <div className="bg-white/80 rounded-lg p-2 mb-2 border border-amber-200">
                      <p className="text-[10px] text-gray-700 font-semibold mb-1">Steps to upgrade:</p>
                      <ol className="text-[10px] text-gray-700 space-y-0.5 list-decimal list-inside">
                        <li>Cancel your current {currentTier.toUpperCase()} subscription</li>
                        <li>Wait for confirmation of cancellation</li>
                        <li>Return here to purchase the {planName} plan</li>
                      </ol>
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={hasAcknowledgedCancellation}
                        onChange={(e) => setHasAcknowledgedCancellation(e.target.checked)}
                        className="mt-0.5 size-3.5 rounded border-2 border-amber-500 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-[10px] text-black font-semibold">
                        I confirm that I have canceled my previous subscription and understand I may be charged for both plans if I proceed without canceling.
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Method Selection - Single Line */}
            <div className="mb-3">
              <label className="block text-xs font-semibold text-black mb-2">Payment Method</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setPaymentMethod('card')}
                  className={`flex-1 p-2 border-2 rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                    paymentMethod === 'card'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-300 bg-white hover:border-blue-400'
                  }`}
                >
                  <CreditCard className="size-4 text-black" />
                  <span className="text-xs font-semibold text-black">Credit/Debit</span>
                </button>
                <button
                  onClick={() => setPaymentMethod('paypal')}
                  className={`flex-1 p-2 border-2 rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                    paymentMethod === 'paypal'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-300 bg-white hover:border-blue-400'
                  }`}
                >
                  <Wallet className="size-4 text-black" />
                  <span className="text-xs font-semibold text-black">PayPal</span>
                </button>
              </div>
            </div>

            {/* Payment Forms */}
            {paymentMethod === 'card' ? (
              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Email */}
                <div>
                  <label className="block text-xs font-semibold text-black mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-black"
                    required
                  />
                </div>

                {/* Script (60-70 words) */}
                <div>
                  <label className="block text-xs font-semibold text-black mb-1">
                    Script (60-70 words)
                  </label>
                  <textarea
                    value={formData.script}
                    onChange={(e) => handleInputChange('script', e.target.value)}
                    placeholder="Your video will communicate this exact script - check it twice!"
                    className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-black resize-none h-24 placeholder:text-gray-400"
                  />
                </div>

                {/* Existing Avatar ID */}
                <div>
                  <label className="block text-xs font-semibold text-black mb-1">
                    Existing Avatar ID (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.avatarId}
                    onChange={(e) => handleInputChange('avatarId', e.target.value)}
                    placeholder="Enter your avatar ID (if applicable)"
                    className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-black"
                  />
                  <p className="text-[10px] text-gray-600 mt-1">
                    If you have an existing avatar, enter the ID here to link it to your account
                  </p>
                </div>

                {/* Card Name */}
                <div>
                  <label className="block text-xs font-semibold text-black mb-1">
                    Cardholder Name *
                  </label>
                  <input
                    type="text"
                    value={formData.cardName}
                    onChange={(e) => handleInputChange('cardName', e.target.value)}
                    placeholder="John Doe"
                    className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-black"
                    required
                  />
                </div>

                {/* Card Number */}
                <div>
                  <label className="block text-xs font-semibold text-black mb-1">
                    Card Number *
                  </label>
                  <input
                    type="text"
                    value={formData.cardNumber}
                    onChange={(e) => handleCardNumberChange(e.target.value)}
                    placeholder="1234 5678 9012 3456"
                    className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-black font-mono"
                    required
                  />
                </div>

                {/* Expiry and CVV */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-semibold text-black mb-1">
                      Expiry Date *
                    </label>
                    <input
                      type="text"
                      value={formData.expiryDate}
                      onChange={(e) => handleExpiryChange(e.target.value)}
                      placeholder="MM/YY"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-black font-mono"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-black mb-1">
                      CVV *
                    </label>
                    <input
                      type="text"
                      value={formData.cvv}
                      onChange={(e) => handleCvvChange(e.target.value)}
                      placeholder="123"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-black font-mono"
                      required
                    />
                  </div>
                </div>

                {/* ZIP Code */}
                <div>
                  <label className="block text-xs font-semibold text-black mb-1">
                    Billing ZIP Code *
                  </label>
                  <input
                    type="text"
                    value={formData.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    placeholder="12345"
                    className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-black"
                    required
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isProcessing || (hasExistingSubscription && !hasAcknowledgedCancellation)}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <Lock className="size-4" />
                      {hasExistingSubscription ? `Upgrade Now! - $${planPrice}/mo` : `Pay $${planPrice}/mo`}
                    </>
                  )}
                </button>

                {/* Security Badge */}
                <div className="flex items-center justify-center gap-2 text-[10px] text-gray-600 pt-1">
                  <Lock className="size-3" />
                  <span>Secured by 256-bit SSL encryption</span>
                </div>
              </form>
            ) : (
              // PayPal Option
              <div className="space-y-3">
                {/* Email for PayPal */}
                <div>
                  <label className="block text-xs font-semibold text-black mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-black"
                    required
                  />
                </div>

                {/* PayPal Button */}
                <button
                  onClick={handlePayPalCheckout}
                  disabled={isProcessing || !formData.email || (hasExistingSubscription && !hasAcknowledgedCancellation)}
                  className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      {hasExistingSubscription ? 'Upgrade Now!' : 'Continue with PayPal'}
                    </>
                  )}
                </button>

                <p className="text-[10px] text-gray-600 text-center">
                  You'll be redirected to PayPal to complete your purchase
                </p>
              </div>
            )}

            {/* Trust Indicators */}
            <div className="mt-4 pt-4 border-t border-gray-300">
              <div className="grid grid-cols-3 gap-2 text-center text-[10px] text-gray-600">
                <div>
                  <div className="text-xl mb-0.5">🔒</div>
                  <div className="font-semibold text-black text-[9px]">Secure</div>
                  <div>256-bit SSL</div>
                </div>
                <div>
                  <div className="text-xl mb-0.5">↩️</div>
                  <div className="font-semibold text-black text-[9px]">Money Back</div>
                  <div>14-day guarantee</div>
                </div>
                <div>
                  <div className="text-xl mb-0.5">🚫</div>
                  <div className="font-semibold text-black text-[9px]">Cancel Anytime</div>
                  <div>No commitment</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* User Portal - Shows after successful purchase */}
      {showPortal && (
        <UserPortal
          onClose={() => {
            setShowPortal(false);
            onClose();
          }}
        />
      )}
    </>
  );
}